
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Navbar from './components/Navbar';
import ArtCard from './components/ArtCard';
import CustomRequest from './components/CustomRequest';
import HeroCarousel from './components/HeroCarousel';
import ImageModal from './components/ImageModal';
import MouseParticles from './components/MouseParticles';
import AdminPanel from './components/AdminPanel';
import AdminLogin from './components/AdminLogin';
import UserAuthModal from './components/UserAuthModal';
import SaveIndicator from './components/SaveIndicator';
import UserOrders from './components/UserOrders';
import UserProfile from './components/UserProfile';
import ChatOverlay from './components/ChatOverlay';
import CommentsSection from './components/CommentsSection';
import { Footer } from './components/Footer';
import { FaqSection } from './components/FaqSection';
import { database } from './services/databaseService';
import { storage } from './services/storageService';
import { ArtItem, CartItem, ArtCategory, Language, Order, OrderStatus, ChatThread, ChatMessage, Comment, User, CommissionTier, SiteSettings } from './types';
import { INITIAL_ARTS, DATA_VERSION, DEFAULT_CATEGORY_COVERS, DEFAULT_COMMISSION_TIERS, DEFAULT_COMMENTS, ART_SUBCATEGORIES, ART_TYPES } from './constants';
import { Flower, Settings, ArrowRight, Loader2, Search, Trash2, X, ShoppingBag, CheckCircle, CloudDownload, Sparkles, RefreshCw, Server, ShieldCheck, Lock, Unlock, PlayCircle, LayoutGrid, ChevronLeft, ChevronRight } from 'lucide-react';

const App: React.FC = () => {
  // Função para pegar a página inicial baseada no Hash da URL
  const getInitialPageFromHash = () => {
    try {
      const hash = window.location.hash.replace('#', '');
      const page = hash.split('/')[0];
      const validPages = ['home', 'gallery', 'custom-art', 'faq', 'my-orders', 'profile'];
      return validPages.includes(page) ? page : 'home';
    } catch (e) {
      return 'home';
    }
  };

  const [activePage, setActivePage] = useState(getInitialPageFromHash());
  const [arts, setArts] = useState<ArtItem[]>([]); 
  const [cart, setCart] = useState<CartItem[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [chatThreads, setChatThreads] = useState<ChatThread[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedArt, setSelectedArt] = useState<ArtItem | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<ArtCategory | null>(() => {
    try {
      const hash = window.location.hash.replace('#', '');
      const page = hash.split('/')[0];
      return page === 'gallery' ? ArtCategory.ANIMATED : null;
    } catch (e) {
      return null;
    }
  });
  const [subCategoryFilter, setSubCategoryFilter] = useState<string | null>(null);
  const [artTypeFilter, setArtTypeFilter] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [categoryCovers, setCategoryCovers] = useState<Record<string, string>>(DEFAULT_CATEGORY_COVERS);
  const [commissionTiers, setCommissionTiers] = useState<CommissionTier[]>([]);
  const [siteSettings, setSiteSettings] = useState<SiteSettings>({
    customRequestIntro_pt: 'Deseja algo que transcenda a galeria comum? Entre em contato com nossos mestres artesãos para solicitar uma arte personalizada para o seu perfil.',
    customRequestIntro_en: 'Looking for something beyond the common gallery? Contact our master craftsmen to request a personalized art for your profile.'
  });
  const [language, setLanguage] = useState<Language>('en'); // Idioma inicial trocado para Inglês
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isUserAuthOpen, setIsUserAuthOpen] = useState(false);
  const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false);
  const [isAdminLoginOpen, setIsAdminLoginOpen] = useState(false);
  const [syncStatus, setSyncStatus] = useState<'cloud' | 'local'>('local');
  const [notifications, setNotifications] = useState({ orders: 0, chats: 0 });
  const artsGridRef = useRef<HTMLDivElement>(null);
  const subCategoriesRef = useRef<HTMLDivElement>(null);

  const smoothScrollToRef = (targetRef: React.RefObject<HTMLDivElement | null>, offset = 110) => {
    if (!targetRef.current) return;
    const elementPosition = targetRef.current.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = elementPosition - offset;
    
    // Custom buttery-smooth scroll transition with cubic ease-in-out
    const startY = window.scrollY;
    const difference = offsetPosition - startY;
    const duration = 1000; // 1 second duration for a premium, majestic gliding feel
    const startTime = performance.now();

    const easeInOutCubic = (t: number): number => {
      return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
    };

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease = easeInOutCubic(progress);

      window.scrollTo(0, startY + difference * ease);

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  };

  const handleCategoryChange = (cat: ArtCategory | null) => {
    const nextCat = categoryFilter === cat ? null : cat;
    setCategoryFilter(nextCat);
    setSubCategoryFilter(null);
    setArtTypeFilter(null);
    setTimeout(() => {
      if (nextCat) {
        smoothScrollToRef(subCategoriesRef, 110);
      } else {
        smoothScrollToRef(artsGridRef, 110);
      }
    }, 120);
  };

  const handleSubCategoryChange = (subId: string | null) => {
    setSubCategoryFilter(subId);
    setTimeout(() => {
      smoothScrollToRef(artsGridRef, 110);
    }, 120);
  };

  const handleArtTypeChange = (typeId: string | null) => {
    setArtTypeFilter(typeId);
    setTimeout(() => {
      smoothScrollToRef(artsGridRef, 110);
    }, 120);
  };

  const handleAddComment = async (text: string, rating: number) => {
    if (!currentUser) return;
    
    const newComment: Comment = {
      id: 'REV-' + Date.now(),
      userName: currentUser.name,
      userEmail: currentUser.email,
      text: text,
      date: new Date().toISOString().split('T')[0],
      rating: rating,
      verified: true
    };

    setComments(prev => [newComment, ...prev]);
    await database.saveComment(newComment);
  };

  // Monitora mudanças no Hash da URL (Botão voltar/avançar do navegador)
  useEffect(() => {
    const handleHashChange = () => {
      const newPage = getInitialPageFromHash();
      if (newPage === 'gallery' && activePage !== 'gallery') {
        setCategoryFilter(ArtCategory.ANIMATED);
        setSubCategoryFilter(null);
        setArtTypeFilter(null);
      }
      setActivePage(newPage);

      // Verificar se há uma arte específica na URL
      const hash = window.location.hash.replace('#', '');
      if (hash.includes('/')) {
        const [page, artId] = hash.split('/');
        if (page === 'gallery' && artId && arts.length > 0) {
          const art = arts.find(a => a.id === artId);
          if (art) setSelectedArt(art);
        }
      } else if (selectedArt) {
        // Se o hash mudou para algo sem barra e havia uma arte selecionada, fecha o modal
        setSelectedArt(null);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [arts, selectedArt, activePage]);

  // Listen for typing sequence '4708' to trigger Admin Login
  useEffect(() => {
    let keysPressed = '';
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore shortcut if user is typing in interactive form fields
      const activeEl = document.activeElement;
      if (activeEl && (
        activeEl.tagName === 'INPUT' || 
        activeEl.tagName === 'TEXTAREA' || 
        activeEl.tagName === 'SELECT' ||
        activeEl.getAttribute('contenteditable') === 'true'
      )) {
        return;
      }

      keysPressed += e.key.toLowerCase();
      if (keysPressed.length > 4) {
        keysPressed = keysPressed.slice(-4);
      }

      if (keysPressed === '4708') {
        e.preventDefault();
        setIsAdminLoginOpen(true);
        keysPressed = '';
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Sincronizar selectedArt com a URL
  useEffect(() => {
    if (isLoaded && arts.length > 0) {
      const hash = window.location.hash.replace('#', '');
      if (hash.includes('/')) {
        const [page, artId] = hash.split('/');
        if (page === 'gallery' && artId) {
          const art = arts.find(a => a.id === artId);
          if (art) setSelectedArt(art);
        }
      }
    }
  }, [isLoaded, arts]);

  // Atualiza a URL quando uma arte é selecionada ou fechada
  useEffect(() => {
    if (!isLoaded) return;
    
    const currentHash = window.location.hash.replace('#', '');
    const [page] = currentHash.split('/');
    
    if (selectedArt) {
      const newHash = `${activePage}/${selectedArt.id}`;
      if (currentHash !== newHash) {
        window.location.hash = newHash;
      }
    } else if (currentHash.includes('/')) {
      // Se fechou a arte e a URL ainda tem o ID, volta para a página ativa
      window.location.hash = activePage;
    }
  }, [selectedArt, isLoaded]);

  const loadInitialCache = useCallback(async () => {
    let hasArtsCache = false;
    try {
      // 1. Carrega todas as informações locais do IndexedDB (Ultra-rápido, ~15ms)
      const [
        cachedArts,
        cachedCovers,
        cachedTiers,
        cachedSettings,
        cachedComments,
        cachedOrders,
        cachedChats,
        user
      ] = await Promise.all([
        storage.load('arts'),
        storage.load('category_covers', 'registry'),
        storage.load('commission_tiers', 'registry'),
        storage.load('settings', 'registry'),
        storage.load('comments'),
        storage.load('orders'),
        storage.load('chats'),
        database.getCurrentUser()
      ]);

      // 2. Define o usuário atual
      if (user) setCurrentUser(user);

      // 3. Processa e aplica as artes locais de imediato
      if (cachedArts && cachedArts.length > 0) {
        const deleted: string[] = await storage.load('deleted_arts', 'registry') || [];
        const filteredCached = cachedArts.filter((a: any) => !deleted.includes(a.id));
        const processedCached = filteredCached.map((art: any) => database.processArtItem(art));
        setArts(processedCached);
        hasArtsCache = true;
      } else {
        setArts(INITIAL_ARTS.map(art => database.processArtItem(art)));
      }

      // 4. Capas de Categorias do cache
      if (cachedCovers) {
        const resolved: Record<string, string> = { ...DEFAULT_CATEGORY_COVERS };
        Object.entries(cachedCovers).forEach(([cat, path]) => {
          if (path) {
            resolved[cat] = database.resolveMediaUrl(path as string);
          }
        });
        setCategoryCovers(resolved);
      }

      // 5. Tiers de comissão do cache
      if (cachedTiers && cachedTiers.length > 0) {
        const resolvedTiers = cachedTiers.map((t: any) => ({
          ...t,
          demoUrl: database.resolveMediaUrl(t.demoUrl)
        }));
        setCommissionTiers(resolvedTiers);
      } else {
        setCommissionTiers(DEFAULT_COMMISSION_TIERS.map(t => ({
          ...t,
          demoUrl: database.resolveMediaUrl(t.demoUrl)
        })));
      }

      // 6. Configurações do site do cache
      if (cachedSettings) {
        setSiteSettings(cachedSettings);
      }

      // 7. Avaliações/Comentários do cache
      if (cachedComments && cachedComments.length > 0) {
        const cleanComments = cachedComments
          .filter((c: any) => c && c.id && !c.id.startsWith('SEED-'))
          .filter((comment: any, index: number, self: any[]) => self.findIndex(c => c.id === comment.id) === index);
        setComments(cleanComments);
      }

      // 8. Pedidos do cache
      if (cachedOrders && cachedOrders.length > 0) {
        const deleted: string[] = await storage.load('deleted_orders', 'registry') || [];
        const filteredOrders = cachedOrders.filter((o: any) => !deleted.includes(o.id));
        setOrders(filteredOrders);
      }

      // 9. Chats do cache
      if (cachedChats && cachedChats.length > 0) {
        setChatThreads(cachedChats);
      }
    } catch (e) {
      console.warn("Falha ao carregar cache inicial:", e);
      setArts(INITIAL_ARTS.map(art => database.processArtItem(art)));
    } finally {
      // Se já temos artes no cache local, unbloqueamos o loader imediatamente!
      if (hasArtsCache) {
        setIsLoaded(true);
      }
    }
    return hasArtsCache;
  }, []);

  const syncData = useCallback(async (hasArtsCache: boolean) => {
    setIsSyncing(true);
    try {
      // Registrar visita (Analytics) - Fire-and-forget para não bloquear o carregamento principal!
      database.logVisit().catch(err => console.warn("Analytics log failed", err));

      // 1. Carrega as Artes Atuais (Prioridade Máxima!)
      // Desacoplado das outras requisições para carregar instantaneamente, sem esperar nada!
      database.getArts().then((artsRes) => {
        if (artsRes && artsRes.data) {
          setArts(artsRes.data);
          setSyncStatus(artsRes.source === 'hostinger_server' ? 'cloud' : 'local');
        }
      }).catch(err => {
        console.error("Error syncing arts:", err);
      }).finally(() => {
        // Se NÃO tínhamos cache (novo usuário), esperamos a resposta das artes atuais antes de fechar o loader
        if (!hasArtsCache) {
          setIsLoaded(true);
        }
      });

      // 2. Carrega as Outras Informações Essenciais de forma silenciosa e paralela
      database.getCategoryCovers().then((covers) => {
        if (covers) setCategoryCovers(covers);
      }).catch(err => console.error("Error syncing covers:", err));

      database.getCommissionTiers().then((tiers) => {
        if (tiers) setCommissionTiers(tiers);
      }).catch(err => console.error("Error syncing tiers:", err));

      database.getSettings().then((settings) => {
        if (settings) setSiteSettings(settings);
      }).catch(err => console.error("Error syncing settings:", err));

      // 3. Dados do Usuário conectados sob demanda (Pedidos e Chats só se houver login ativo)
      database.getCurrentUser().then((user) => {
        if (user) {
          setCurrentUser(user);
          // Só solicita pedidos e chats do servidor Hostinger se houver usuário autenticado!
          database.getOrders().then((ordersRes) => {
            if (ordersRes && ordersRes.data) setOrders(ordersRes.data);
          }).catch(err => console.error("Error syncing orders:", err));

          database.getChatThreads().then((chatsRes) => {
            if (chatsRes) setChatThreads(chatsRes);
          }).catch(err => console.error("Error syncing chats:", err));
        }
      }).catch(err => console.error("Error checking user session:", err));

      // 4. Carrega os Comentários/Avaliações
      database.getComments().then((commRes) => {
        if (commRes && commRes.data && commRes.data.length > 0) {
          const cleanComments = commRes.data
            .filter(c => c && c.id && !c.id.startsWith('SEED-'))
            .filter((comment, index, self) => self.findIndex(c => c.id === comment.id) === index);
          setComments(cleanComments);
          if (cleanComments.length !== commRes.data.length) {
            database.saveAllComments(cleanComments).catch(err => console.warn(err));
          }
        }
      }).catch(err => console.error("Error syncing comments:", err));

      // Timeout de segurança se as artes falharem para novos visitantes não travarem infinitamente
      if (!hasArtsCache) {
        setTimeout(() => {
          setIsLoaded(true);
        }, 4000);
      }

    } catch (e) {
      console.error("Background sync failure:", e);
      if (!hasArtsCache) {
        setIsLoaded(true);
      }
    } finally {
      setIsSyncing(false);
    }
  }, []);

  useEffect(() => {
    const run = async () => {
      const hasCache = await loadInitialCache();
      syncData(hasCache);
    };
    run();
  }, [loadInitialCache, syncData]);

  useEffect(() => {
    // Evita chamadas de polling pesadas no servidor se for apenas um visitante anônimo
    if (!currentUser && !isAdminPanelOpen) return;

    const fetchImmediately = async () => {
      try {
        const [chats, ordersRes] = await Promise.all([
          database.getChatThreads(),
          database.getOrders()
        ]);
        
        if (chats) {
          setChatThreads(prev => {
            const isFreshLoad = prev.length === 0;
            if (chats.length > prev.length && !isFreshLoad) {
              setNotifications(n => ({ ...n, chats: n.chats + (chats.length - prev.length) }));
            } else if (!isFreshLoad) {
              // Check if any thread has new messages
              chats.forEach(newThread => {
                const oldThread = prev.find(t => t.userEmail === newThread.userEmail);
                if (oldThread && newThread.messages.length > oldThread.messages.length) {
                  const lastMsg = newThread.messages[newThread.messages.length - 1];
                  if (lastMsg.sender === 'user') {
                    setNotifications(n => ({ ...n, chats: n.chats + 1 }));
                  }
                }
              });
            }
            return chats;
          });
        }

        if (ordersRes && ordersRes.data) {
          setOrders(prev => {
            const isFreshLoad = prev.length === 0;
            if (ordersRes.data.length > prev.length && !isFreshLoad) {
              setNotifications(n => ({ ...n, orders: n.orders + (ordersRes.data.length - prev.length) }));
            }
            return ordersRes.data;
          });
        }
      } catch (err) {
        console.warn("Immediate polling call failed:", err);
      }
    };

    fetchImmediately();

    const interval = setInterval(fetchImmediately, 10000);
    return () => clearInterval(interval);
  }, [currentUser, isAdminPanelOpen]);

  useEffect(() => {
    const total = notifications.orders + notifications.chats;
    if (total > 0) {
      document.title = `(${total}) Niiko Artwork`;
    } else {
      document.title = `Niiko Artwork`;
    }
  }, [notifications]);

  const isVideoUrl = (url?: string) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.includes('.mp4') || lower.includes('blob:') || lower.startsWith('data:video/');
  };

  const handleSendMessage = useCallback(async (text: string, imageUrl?: string) => {
    if (!currentUser) return;
    
    const nm: ChatMessage = { 
      id: `USER-${Date.now()}`, 
      sender: 'user', 
      text, 
      imageUrl, 
      timestamp: new Date().toLocaleTimeString() 
    };

    const success = await database.saveChatMessage(currentUser.email, currentUser.name, nm);
    
    if (!success) {
      console.error("Failed to save message to server");
      // Opcional: alert(language === 'en' ? "Failed to send message." : "Erro ao enviar mensagem.");
    }

    setChatThreads(prev => {
      const up = [...prev];
      const idx = up.findIndex(ct => ct.userEmail === currentUser.email);
      if (idx > -1) {
        up[idx] = { ...up[idx], messages: [...up[idx].messages, nm], lastUpdated: new Date().toISOString() };
      } else {
        up.push({ userEmail: currentUser.email, userName: currentUser.name, messages: [nm], lastUpdated: new Date().toISOString(), unreadCount: 1 });
      }
      return up;
    });
  }, [currentUser]);

  const handleCheckout = async () => {
    if (!currentUser) { 
      setIsCartOpen(false); 
      setIsUserAuthOpen(true); 
      return; 
    }
    
    setIsSaving(true);
    const newOrder: Order = {
      id: `ORD-${Date.now()}`,
      userId: currentUser.email,
      userName: currentUser.name,
      userEmail: currentUser.email,
      items: [...cart],
      total: cart.reduce((a, b) => a + (b.price * b.quantity), 0),
      date: new Date().toLocaleString(),
      status: OrderStatus.PENDING
    };

    try {
      const success = await database.saveOrder(newOrder);
      if (success) {
        setOrders(prev => [newOrder, ...prev]);
        const orderSummary = cart.map(i => i.title[language]).join(', ');
        handleSendMessage(`🚀 NEW ORDER: #${newOrder.id}. Items: ${orderSummary}.`);
        setCart([]);
        setIsCartOpen(false);
        handleNavigate('my-orders');
      }
    } catch (e) {
      alert(language === 'en' ? "Error sealing order." : "Erro ao selar pedido.");
    } finally {
      setIsSaving(false);
    }
  };

  // Função centralizada para navegar e mudar a URL
  const handleNavigate = (page: string) => {
    setActivePage(page);
    if (page === 'gallery') {
      setCategoryFilter(ArtCategory.ANIMATED);
      setSubCategoryFilter(null);
      setArtTypeFilter(null);
    }
    try {
      window.location.hash = page; // Atualiza a URL com o Hash
    } catch (e) {
      // Silently fail if location.hash is inaccessible (e.g. in blob URLs)
      console.warn("Navigation warning: Unable to set location hash.", e);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredArts = useMemo(() => {
    return arts.filter(art => {
      const isAnimatedAll = categoryFilter === ArtCategory.ANIMATED;
      const matchesCategory = isAnimatedAll ? true : (categoryFilter ? art.category === categoryFilter : true);
      const matchesSubcategory = subCategoryFilter
        ? (art.subcategory?.toLowerCase() === subCategoryFilter.toLowerCase())
        : true;
      const matchesArtType = artTypeFilter
        ? (art.artType?.toLowerCase() === artTypeFilter.toLowerCase())
        : true;
      const title = art.title[language] || '';
      return matchesCategory && matchesSubcategory && matchesArtType && title.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [arts, categoryFilter, subCategoryFilter, artTypeFilter, searchQuery, language]);

  const ITEMS_PER_PAGE = 8;
  
  const paginatedArts = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredArts.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredArts, currentPage]);

  const totalPages = useMemo(() => {
    return Math.ceil(filteredArts.length / ITEMS_PER_PAGE);
  }, [filteredArts.length]);

  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter, subCategoryFilter, artTypeFilter, searchQuery]);

  const handlePageChange = (pageNum: number) => {
    setCurrentPage(pageNum);
    setTimeout(() => {
      if (artsGridRef.current) {
        const elementPosition = artsGridRef.current.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - 110;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }, 50);
  };

  if (!isLoaded) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex flex-col items-center justify-center gap-12 z-[999]">
        <Flower className="w-20 h-20 text-[#ff9eb5] animate-spin" />
        <p className="text-white/80 font-bold uppercase tracking-[0.4em] text-[10px]">
          {language === 'en' ? 'Connecting to the Sanctuary...' : 'Conectando ao Santuário...'}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent selection:bg-[#ff9eb5] selection:text-white relative">
      <MouseParticles />
      <SaveIndicator active={isSaving} language={language} />

      <Navbar 
        cartCount={cart.length} 
        onCartClick={() => currentUser ? setIsCartOpen(true) : setIsUserAuthOpen(true)}
        onChatClick={() => (currentUser ? setIsChatOpen(true) : setIsUserAuthOpen(true))}
        onAdminClick={() => setIsAdminLoginOpen(true)}
        onNavigate={handleNavigate}
        onUserAuthClick={() => setIsUserAuthOpen(true)}
        currentUser={currentUser}
        onLogout={() => { database.signOut(); setCurrentUser(null); handleNavigate('home'); }}
        activePage={activePage}
        language={language}
        onToggleLanguage={() => setLanguage(l => l === 'pt' ? 'en' : 'pt')}
      />

      <main className="max-w-[1600px] mx-auto px-4 md:px-8 py-12 md:py-20 relative z-10">
        {activePage === 'home' && (
          <div className="space-y-48">
            <div className="flex flex-col items-center text-center space-y-12 py-10 md:py-12">
              <HeroCarousel arts={arts.slice(0, 3)} language={language} />
              
              <button onClick={() => handleNavigate('gallery')} className="bg-white hover:bg-[#ff9eb5] text-black hover:text-white px-16 py-8 text-xs font-black uppercase tracking-[0.5em] transition-all flex items-center gap-8 group shadow-2xl admin-glow">
                {language === 'en' ? 'Enter Gallery' : 'Entrar na Galeria'} <ArrowRight className="w-6 h-6 group-hover:translate-x-4 transition-transform" />
              </button>
            </div>
            
            <CommentsSection 
              comments={comments}
              onAddComment={handleAddComment}
              currentUser={currentUser}
              language={language}
              onOpenAuth={() => setIsUserAuthOpen(true)}
            />

            <Footer settings={siteSettings} language={language} />
          </div>
        )}

        {activePage === 'gallery' && (
          <div className="space-y-32">
            <div className="glass-panel p-8 md:p-16 flex flex-col gap-12">
               <div className="relative w-full max-w-4xl mx-auto">
                 <Search className="absolute left-10 top-1/2 -translate-y-1/2 w-8 h-8 text-zinc-500" />
                 <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder={language === 'en' ? "Explore collection..." : "Explorar acervo..."} className="w-full bg-black/50 border border-zinc-800 py-8 pl-24 pr-10 text-xl text-white outline-none focus:border-[#ff9eb5] transition-all" />
               </div>
               <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-6">
                <button 
                  onClick={() => handleCategoryChange(null)} 
                  className="hidden"
                >
                  <LayoutGrid className="absolute inset-0 w-8 h-8 m-auto text-white z-20" />
                  <span className="absolute bottom-2 inset-x-0 flex items-center justify-center text-[10px] font-black uppercase text-white z-20">All</span>
                </button>
                {Object.values(ArtCategory).map(cat => (
                  <button key={cat} onClick={() => handleCategoryChange(cat)} className={`relative aspect-[9/14] border-2 transition-all overflow-hidden ${categoryFilter === cat ? 'border-[#ff9eb5] scale-105' : (categoryFilter === null ? 'border-white/10 hover:border-[#ff9eb5]/50' : 'border-white/5 opacity-40')}`}>
                    {isVideoUrl(categoryCovers[cat] || DEFAULT_CATEGORY_COVERS[cat]) ? (
                      <video 
                        src={categoryCovers[cat] || DEFAULT_CATEGORY_COVERS[cat]} 
                        autoPlay loop muted 
                        className="absolute inset-0 w-full h-full object-cover opacity-30" 
                      />
                    ) : (
                      <img 
                        src={categoryCovers[cat] || DEFAULT_CATEGORY_COVERS[cat]} 
                        className="absolute inset-0 w-full h-full object-cover opacity-30" 
                      />
                    )}
                    <span className="absolute inset-0 flex items-center justify-center text-[10px] font-black uppercase text-white z-20 text-center px-2">{cat}</span>
                  </button>
                ))}
               </div>

               {categoryFilter && ART_SUBCATEGORIES[categoryFilter] && (
                 <div ref={subCategoriesRef} className="border-t border-white/5 pt-10 flex flex-col gap-6 animate-in fade-in slide-in-from-top-4 duration-500">
                   <div className="flex items-center gap-4 text-zinc-500 text-[10px] font-black uppercase tracking-widest font-mono">
                     <span className="w-2 h-2 rounded-full bg-[#ff9eb5] animate-pulse"></span>
                     {language === 'en' ? 'Subcategories' : 'Subcategorias'}
                   </div>
                   <div className="flex flex-wrap gap-4">
                     {[
                       { id: null, label: { pt: 'Todas', en: 'All' } },
                       ...ART_SUBCATEGORIES[categoryFilter]
                     ].map(sub => {
                       const isActive = subCategoryFilter === sub.id;
                       return (
                         <button
                           key={sub.id || 'all'}
                           onClick={() => handleSubCategoryChange(sub.id)}
                           className={`px-8 py-3.5 text-[10px] font-black uppercase tracking-widest border transition-all cursor-pointer duration-300 transform hover:scale-105 active:scale-95 flex items-center gap-3 ${
                             isActive 
                               ? 'bg-[#ff9eb5] text-black border-[#ff9eb5] font-black shadow-[0_0_15px_rgba(255,158,181,0.25)]' 
                               : 'bg-black/40 text-zinc-400 border-white/5 hover:border-[#ff9eb5]/50 hover:text-white'
                           }`}
                         >
                           {sub.id && (
                             <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-black' : 'bg-zinc-600'}`}></span>
                           )}
                           {sub.label[language]}
                         </button>
                       );
                     })}
                   </div>
                 </div>
               )}

               {categoryFilter && (
                 <div className="border-t border-white/5 pt-10 flex flex-col gap-6 animate-in fade-in slide-in-from-top-4 duration-500">
                   <div className="flex items-center gap-4 text-zinc-500 text-[10px] font-black uppercase tracking-widest font-mono">
                     <span className="w-2 h-2 rounded-full bg-[#ff9eb5] animate-pulse"></span>
                     {language === 'en' ? 'Art Type' : 'Tipo de Arte'}
                   </div>
                   <div className="flex flex-wrap gap-4">
                     {[
                       { id: null, label: { pt: 'Todas', en: 'All' } },
                       ...ART_TYPES
                     ].map(typeItem => {
                       const isActive = artTypeFilter === typeItem.id;
                       return (
                         <button
                           key={typeItem.id || 'all-types'}
                           onClick={() => handleArtTypeChange(typeItem.id)}
                           className={`px-8 py-3.5 text-[10px] font-black uppercase tracking-widest border transition-all cursor-pointer duration-300 transform hover:scale-105 active:scale-95 flex items-center gap-3 ${
                             isActive 
                               ? 'bg-[#ff9eb5] text-black border-[#ff9eb5] font-black shadow-[0_0_15px_rgba(255,158,181,0.25)]' 
                               : 'bg-black/40 text-zinc-400 border-white/5 hover:border-[#ff9eb5]/50 hover:text-white'
                           }`}
                         >
                           {typeItem.id && (
                             <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-black' : 'bg-zinc-600'}`}></span>
                           )}
                           {typeItem.label[language]}
                         </button>
                       );
                     })}
                   </div>
                 </div>
               )}


            </div>
            <div ref={artsGridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-24">
              {paginatedArts.map(art => (
                <ArtCard key={art.id} item={art} onAddToCart={setSelectedArt} onPreview={setSelectedArt} language={language} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-6 border-t border-white/5 pt-12 animate-in fade-in duration-500">
                <span className="text-zinc-500 font-mono text-xs uppercase tracking-wider">
                  {language === 'en' 
                    ? `Showing ${Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, filteredArts.length)}-${Math.min(currentPage * ITEMS_PER_PAGE, filteredArts.length)} of ${filteredArts.length} artworks`
                    : `Mostrando ${Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, filteredArts.length)}-${Math.min(currentPage * ITEMS_PER_PAGE, filteredArts.length)} de ${filteredArts.length} artes`}
                </span>
                
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="w-10 h-10 border border-white/10 hover:border-[#ff9eb5] bg-black/40 hover:bg-[#ff9eb5]/10 flex items-center justify-center text-white disabled:opacity-20 disabled:hover:bg-transparent disabled:hover:border-white/10 transition-all group animate-all hover:scale-105 duration-300"
                    title={language === 'en' ? 'Previous Page' : 'Página Anterior'}
                  >
                    <ChevronLeft className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" />
                  </button>

                  <div className="flex items-center gap-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                      const idVal = `page-btn-${pageNum}`;
                      return (
                        <button
                          id={idVal}
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-10 h-10 font-bold font-mono text-xs transition-all border hover:scale-105 duration-300 ${
                            currentPage === pageNum
                              ? 'bg-[#ff9eb5] text-black border-[#ff9eb5] font-black shadow-[0_0_15px_rgba(255,158,181,0.4)]'
                              : 'bg-black/40 text-zinc-400 border-white/5 hover:border-[#ff9eb5]/50 hover:text-white'
                          }`}
                        >
                          {String(pageNum).padStart(2, '0')}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="w-10 h-10 border border-white/10 hover:border-[#ff9eb5] bg-black/40 hover:bg-[#ff9eb5]/10 flex items-center justify-center text-white disabled:opacity-20 disabled:hover:bg-transparent disabled:hover:border-white/10 transition-all group animate-all hover:scale-105 duration-300"
                    title={language === 'en' ? 'Next Page' : 'Próxima Página'}
                  >
                    <ChevronRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activePage === 'custom-art' && (
          <CustomRequest 
            language={language} 
            onOpenChat={() => currentUser ? setIsChatOpen(true) : setIsUserAuthOpen(true)} 
            onPreview={setSelectedArt}
            commissionTiers={commissionTiers}
            settings={siteSettings}
          />
        )}

        {activePage === 'faq' && (
          <FaqSection 
            settings={siteSettings} 
            language={language} 
            onOpenChat={() => currentUser ? setIsChatOpen(true) : setIsUserAuthOpen(true)}
          />
        )}
        
        {activePage === 'my-orders' && currentUser && <UserOrders orders={orders.filter(o => o.userEmail.toLowerCase() === currentUser?.email.toLowerCase())} language={language} />}
        {activePage === 'profile' && currentUser && <UserProfile user={currentUser} orders={orders.filter(o => o.userEmail.toLowerCase() === currentUser.email.toLowerCase())} language={language} onUpdateUser={async (d) => { await database.updateUser(d); setCurrentUser(prev => prev ? {...prev, ...d} : null); return true; }} onLogout={() => { database.signOut(); setCurrentUser(null); handleNavigate('home'); }} />}
      </main>

      {selectedArt && (
        <ImageModal 
          item={selectedArt} 
          onClose={() => setSelectedArt(null)}
          onAddToCart={(it, sig, disc, steam, font, df, px, py, sc, cl, notes) => {
            if (!currentUser) { setIsUserAuthOpen(true); return; }
            setCart(prev => [...prev, { ...it, quantity: 1, customSignature: sig, discord: disc, steamUrl: steam, customFont: font, dafontLink: df, sigPosX: px, sigPosY: py, sigScale: sc, sigColor: cl, notes }]);
            setIsCartOpen(true);
          }}
          language={language}
        />
      )}

      {isCartOpen && currentUser && (
        <div className="fixed inset-0 z-[1000] flex justify-end animate-in fade-in duration-500">
          <div className="absolute inset-0 bg-black/95 backdrop-blur-md" onClick={() => setIsCartOpen(false)} />
          <div className="relative w-full max-w-md bg-zinc-950 border-l border-white/5 h-full flex flex-col p-8">
            <h2 className="text-xl font-black text-white uppercase mb-8">{language === 'en' ? 'Cart' : 'Carrinho'}</h2>
            <div className="flex-1 overflow-y-auto space-y-4">
              {cart.map((item, idx) => (
                <div key={idx} className="flex gap-4 p-4 bg-zinc-900 border border-white/5">
                   <div className="w-12 h-12 bg-black"><img src={item.imageUrl} className="w-full h-full object-cover" /></div>
                   <div className="flex-1"><h4 className="text-white text-[10px] uppercase font-black">{item.title[language]}</h4><p className="text-[#ff9eb5] font-mono text-xs">{language === 'en' ? `$ ${item.price.toFixed(2)}` : `R$ ${(item.price * 5).toFixed(2)}`}</p></div>
                   <button onClick={() => setCart(prev => prev.filter((_, i) => i !== idx))} className="text-zinc-600 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <div className="pt-8 border-t border-white/5">
               <div className="flex justify-between mb-8">
                 <span className="text-zinc-500 uppercase text-[10px] font-black">Total</span>
                 <span className="text-white text-2xl font-black">
                   {language === 'en' ? `$ ${cart.reduce((a, b) => a + (b.price * b.quantity), 0).toFixed(2)}` : `R$ ${(cart.reduce((a, b) => a + (b.price * b.quantity), 0) * 5).toFixed(2)}`}
                 </span>
               </div>
               <button onClick={handleCheckout} disabled={cart.length === 0} className="w-full bg-white hover:bg-[#ff9eb5] text-black hover:text-white py-6 font-black uppercase text-xs">
                 {language === 'en' ? 'Checkout' : 'Finalizar Pedido'}
               </button>
            </div>
          </div>
        </div>
      )}

      {isUserAuthOpen && <UserAuthModal onClose={() => setIsUserAuthOpen(false)} onLoginSuccess={(u) => { setCurrentUser(u); setIsUserAuthOpen(false); }} language={language} />}
      {isAdminLoginOpen && <AdminLogin onClose={() => setIsAdminLoginOpen(false)} onLogin={() => { setIsAdminLoginOpen(false); setIsAdminPanelOpen(true); }} />}
      {isAdminPanelOpen && (
        <AdminPanel 
          arts={arts} 
          orders={orders} 
          chatThreads={chatThreads}
          comments={comments}
          categoryCovers={categoryCovers}
          commissionTiers={commissionTiers}
          siteSettings={siteSettings}
          onUpdateArts={(u) => { setArts(prev => { const n = typeof u === 'function' ? u(prev) : u; storage.save('arts', n); return n; }); }}
          onUpdateOrders={setOrders}
          onUpdateSupport={() => {}}
          onUpdateChatThreads={setChatThreads}
          onUpdateComments={setComments}
          onUpdateCategoryCovers={setCategoryCovers}
          onUpdateCommissionTiers={setCommissionTiers}
          onUpdateSiteSettings={setSiteSettings}
          onClose={() => setIsAdminPanelOpen(false)}
          language={language}
          notifications={notifications}
          onClearNotifications={(type) => setNotifications(prev => ({ ...prev, [type]: 0 }))}
        />
      )}
      {isChatOpen && currentUser && <ChatOverlay userName={currentUser.name} messages={chatThreads.find(t => t.userEmail === currentUser.email)?.messages || []} onSendMessage={handleSendMessage} onClose={() => setIsChatOpen(false)} language={language} />}
    </div>
  );
};

export default App;
